import { Eye, ImageOff, RotateCw, Tags } from "lucide-react";
import { useState } from "react";

import type { GroupedItemDto } from "../../shared/types";
import { fmtGone } from "../lib/format";
import { proxiedImageUrl } from "../lib/imageProxy";
import type { Observation } from "../lib/observations";
import { Money, Skeleton, StateLabel, stateStripeClass } from "../ui";
import type { RowState } from "../ui";

export type VariantRowData = {
	productId: number;
	storeId: string;
	title: string;
	priceMinor: number | null;
	inStock: boolean | null;
	observations?: Observation[];
	maxOrderQty?: number | null;
};

/** in_stock true -> live, false -> gone, null (never checked) -> idle. */
export function variantState(inStock: boolean | null): RowState {
	if (inStock === true) return "live";
	if (inStock === false) return "gone";
	return "idle";
}

/** Store lines shown before the rest collapse into "+N more". */
const LEDGER_MAX = 3;

/** The one price a tile leads with, and the state that explains it. Sold-out
 *  is the dominant state, so it leads with what it was last buyable at. */
function headline(group: GroupedItemDto, now: number): { minor: number | null; state: RowState; label: string } {
	if (group.cheapestInStock) {
		return {
			minor: group.cheapestInStock.priceMinor,
			state: "live",
			label: `In stock · ${group.inStockCount}`,
		};
	}
	if (group.lastInStock) {
		return {
			minor: group.lastInStock.priceMinor,
			state: "gone",
			label: `Gone ${fmtGone(group.lastInStock.at, now)}`,
		};
	}
	return {
		minor: group.cheapestOverall?.priceMinor ?? null,
		state: "idle",
		label: "Never in stock",
	};
}

/**
 * One item — a group of store listings — as a tile: the photo, the title, the
 * price you'd pay, and a short ledger of the stores carrying it. The whole
 * tile links to the group; each ledger line links to that store's listing.
 *
 * Only in-stock groups across several stores get per-store lines, since
 * those are the rows you can act on during a drop. Everything else names its
 * stores and nothing more: one store's line would only repeat the headline,
 * and the prices behind a "Gone" are on the product page.
 */
export function ItemTile({
	group,
	variants,
	storeNames,
	now,
	error,
	onRetry,
	onClassify,
}: {
	group: GroupedItemDto;
	/** null until this group's variants have been fetched. */
	variants: VariantRowData[] | null;
	storeNames: Map<string, string>;
	now: number;
	/** Set when this group's variant fetch failed. */
	error?: string | null;
	onRetry?: () => void;
	/** Opens the catalogue classifier for this item — a test tool for now. */
	onClassify?: () => void;
}) {
	const [imageFailed, setImageFailed] = useState(false);
	const src = proxiedImageUrl(group.imageUrl);
	const head = headline(group, now);
	const storeName = (id: string) => storeNames.get(id) ?? id;

	return (
		<article className="group relative grid grid-cols-[64px_minmax(0,1fr)] gap-x-3 overflow-hidden rounded-[10px] border border-hairline bg-paper p-1.5 hover:border-line sm:flex sm:flex-col sm:p-0">
			<div className="relative size-16 overflow-hidden rounded-md bg-plate sm:m-1 sm:mb-0 sm:h-24 sm:w-auto sm:rounded-[7px]">
				{src && !imageFailed ? (
					<img
						src={src}
						alt=""
						loading="lazy"
						onError={() => setImageFailed(true)}
						// Anything you can't buy right now is dimmed back, so the tiles
						// you can buy stand out of the grid by contrast alone — the
						// colour returns on hover.
						className={`size-full object-contain p-1 mix-blend-multiply transition-[filter,opacity] sm:p-2 ${
							head.state === "live" ? "" : "opacity-60 grayscale group-hover:opacity-100 group-hover:grayscale-0"
						}`}
					/>
				) : (
					// The plate stays light in both themes, so this is light --muted.
					<span className="flex size-full items-center justify-center text-[#566079]">
						<ImageOff className="size-5" aria-hidden />
					</span>
				)}
				{onClassify && (
					// Above the tile's stretched link, so it opens the classifier
					// rather than the product page.
					<button
						type="button"
						onClick={onClassify}
						aria-label="Classify this item"
						title="Classify this item"
						className="absolute top-1 left-1 z-10 inline-flex size-7 items-center justify-center rounded-full border border-hairline bg-paper text-ink shadow-[0_2px_6px_rgba(6,9,18,0.25)] hover:bg-raised sm:top-1.5 sm:left-1.5"
					>
						<Tags className="size-3.5" aria-hidden />
					</button>
				)}
				{group.anyWatched && (
					<span className="absolute top-1 right-1 rounded-full bg-ink p-1 text-ground sm:top-1.5 sm:right-1.5">
						<Eye className="size-3" aria-hidden />
						<span className="sr-only">Watched</span>
					</span>
				)}
			</div>

			<div className="flex min-w-0 flex-1 flex-col sm:px-2.5 sm:pt-2 sm:pb-2">
				<h3 className="line-clamp-2 text-[12.5px] leading-[1.3] font-medium sm:min-h-[2lh]">
					{/* Stretched over the whole tile; ledger links sit above it. */}
					<a
						href={`#/items/${group.productId}`}
						className="after:absolute after:inset-0 after:rounded-[10px] focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-live"
					>
						{group.title}
					</a>
				</h3>

				<div className="mt-1 flex items-baseline justify-between gap-2 sm:block">
					<Money
						minor={head.minor}
						className={`block text-[17px] leading-5 ${head.state === "live" ? "" : "text-muted"}`}
					/>
					<StateLabel state={head.state} label={head.label} />
				</div>

				<div className="mt-1.5 border-t border-hairline pt-1 sm:mt-auto">
					{group.inStockCount > 0 && group.storeCount > 1 ? (
						<Ledger group={group} variants={variants} storeName={storeName} error={error} onRetry={onRetry} />
					) : (
						<p className="truncate py-0.5 text-[11px] text-muted" title={group.storeIds.map(storeName).join(", ")}>
							{group.storeIds.map(storeName).join(", ")}
						</p>
					)}
				</div>
			</div>
		</article>
	);
}

function Ledger({
	group,
	variants,
	storeName,
	error,
	onRetry,
}: {
	group: GroupedItemDto;
	variants: VariantRowData[] | null;
	storeName: (id: string) => string;
	error?: string | null;
	onRetry?: () => void;
}) {
	if (error) {
		return (
			<div className="relative z-10 flex items-center justify-between gap-2 text-xs" role="alert">
				<span className="min-w-0 truncate text-fault" title={error}>
					Couldn't load stores
				</span>
				{onRetry && (
					<button
						type="button"
						onClick={onRetry}
						className="inline-flex min-h-6 shrink-0 items-center gap-1 rounded px-1.5 text-ink hover:bg-raised"
					>
						<RotateCw className="size-3" aria-hidden />
						Retry
					</button>
				)}
			</div>
		);
	}

	// The list DTO already carries the cheapest in-stock listing — the one
	// you'd act on — so it renders straight away and the rest fill in behind.
	if (variants === null) {
		const first = group.cheapestInStock;
		const pending = Math.min(group.storeCount, LEDGER_MAX) - (first ? 1 : 0);
		return (
			<ul>
				{first && (
					<LedgerLine
						productId={first.productId}
						store={storeName(first.storeId)}
						state="live"
						priceMinor={first.priceMinor}
					/>
				)}
				{Array.from({ length: pending }, (_, i) => (
					<li key={i} className="flex h-7 items-center sm:h-5">
						<Skeleton className="h-3 w-full" />
					</li>
				))}
			</ul>
		);
	}

	const sorted = [...variants].sort((a, b) => {
		const aLive = a.inStock === true ? 0 : 1;
		const bLive = b.inStock === true ? 0 : 1;
		if (aLive !== bLive) return aLive - bLive;
		return (a.priceMinor ?? Infinity) - (b.priceMinor ?? Infinity);
	});
	const shown = sorted.slice(0, LEDGER_MAX);
	const rest = sorted.length - shown.length;

	return (
		<ul>
			{shown.map((v) => (
				<LedgerLine
					key={v.productId}
					productId={v.productId}
					store={storeName(v.storeId)}
					state={variantState(v.inStock)}
					priceMinor={v.priceMinor}
				/>
			))}
			{rest > 0 && <li className="flex h-7 items-center pl-[11px] text-[11px] text-muted sm:h-5">+{rest} more</li>}
		</ul>
	);
}

/** One store in the ledger. In stock shows the price; anything else shows
 *  the state word instead, so state never rests on colour alone. */
function LedgerLine({
	productId,
	store,
	state,
	priceMinor,
}: {
	productId: number;
	store: string;
	state: RowState;
	priceMinor: number | null;
}) {
	return (
		<li>
			<a
				href={`#/items/${productId}`}
				className="relative z-10 -mx-1 grid h-7 grid-cols-[3px_minmax(0,1fr)_auto] items-center gap-x-1.5 rounded px-1 text-[11px] hover:bg-raised sm:h-5"
			>
				<span className={`h-3 rounded-full ${stateStripeClass(state)}`} />
				<span className="truncate">{store}</span>
				{state === "live" ? (
					<Money minor={priceMinor} className="text-[12px] text-ink" />
				) : (
					<StateLabel state={state} />
				)}
			</a>
		</li>
	);
}
